import React from "react";
import { AnySelectProps, MultiSelectProps, SelectProps } from "./Select";
import { Option as ReactSelectOption } from "react-select";

export type Option = Omit<ReactSelectOption<string>, "label"> & {
  label?: React.ReactNode;
  value?: string;
  error?: boolean;
  icon?: React.ReactElement<any>;
  parent?: string;
  description?: React.ReactNode;
  renderLabel?: () => React.ReactNode;
  divider?: boolean;
  style?: React.CSSProperties;
  skipCheckbox?: boolean;
  hiddenCheckbox?: boolean;
  unselectable?: boolean;
  sortByValue?: string;
};

export function isMulti(props: AnySelectProps): props is MultiSelectProps {
  return props.multi;
}

export type MultiType = "plain" | "chips";

export type OptionType = "plain" | "checkbox" | "checkboxTwoLine";

export type OptionComponentProps = {
  className?: string;
  inputValue?: string;
  isDisabled?: boolean;
  isFocused?: boolean;
  isGroup?: boolean;
  isSelected?: boolean;
  onFocus?: (e: React.SyntheticEvent<any>) => any;
  focusOption?: () => any;
  instancePrefix?: string;
  onSelect?: (option: Option) => any;
  option?: Option;
  optionIndex?: number;
  /**
   * 0 for root element, 1 for immediate children, etc
   */
  parentLevel?: number;
  removeValue?: () => any;
  selectValue?: () => any;
  isGroupOpen?: boolean;
  handleDropdownItemClick?: (e: React.SyntheticEvent<any>) => any;
  children?: React.ReactNode;
} & React.ClassAttributes<Option>;

export type MenuRendererProps = {
  focusOption: () => any;
  focusedOption: Option;
  inputValue: string;
  instancePrefix: string; // className
  labelKey: string;
  onFocus: () => any;
  onOptionRef: (...args: any[]) => any;
  onSelect: () => any;
  optionClassName: string;
  optionComponent: React.ComponentType<OptionComponentProps>;
  optionRenderer: (...any) => any;
  options: Option[];
  removeValue: () => any;
  renderMenu?: (menu: React.ReactElement<any>) => React.ReactElement<any>;
  selectValue: () => any;
  valueArray: Option[];
  valueKey: string;
};

export const ITEM_HEIGHT = 48;
const LIST_HEIGHT = ITEM_HEIGHT * 8;

export const sortOptionsToSortBy = (sortOptions: SelectProps["sortOptions"]) =>
  typeof sortOptions === "boolean"
    ? [(x) => x.sortByValue ?? x.label]
    : Array.isArray(sortOptions)
    ? sortOptions
    : [sortOptions];

/**
 * This global styles have to be injected in order for the component to work properly
 */
export const reactSelectGlobalMuiStyles = (theme) => ({
  ".Select-control": {
    display: "flex",
    alignItems: "center",
    border: 0,
    height: "auto",
    background: "transparent",
    "&:hover": {
      boxShadow: "none"
    }
  },
  ".Select-multi-value-wrapper": {
    flexGrow: 1,
    display: "flex",
    flexWrap: "wrap",
    marginBottom: -4,

    "& > div": {
      marginBottom: 4
    }
  },
  ".Select--multi .Select-input": {
    margin: 0
  },
  ".Select.has-value.is-clearable.Select--single > .Select-control .Select-value": {
    padding: 0
  },
  ".Select-noresults": {
    padding: theme.spacing(2),
    zIndex: "9999 !important"
  },
  ".Select-input": {
    display: "inline-flex !important",
    padding: 0,
    height: "auto"
  },
  ".Select-input input": {
    background: "transparent",
    border: 0,
    padding: 0,
    cursor: "default",
    display: "inline-block",
    fontFamily: "inherit",
    fontSize: "inherit",
    margin: 0,
    outline: 0
  },
  ".Select-placeholder, .Select--single .Select-value": {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    alignItems: "center",
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.pxToRem(16),
    padding: 0
  },
  ".Select-placeholder": {
    opacity: 0.42,
    color: theme.palette.common.black
  },
  ".Select-menu-outer": {
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[2],
    position: "absolute",
    left: 0,
    top: `calc(100% + ${theme.spacing()}px)`,
    minWidth: "100%",
    zIndex: 20,
    maxHeight: LIST_HEIGHT
  },
  ".Select.is-focused:not(.is-open) > .Select-control": {
    boxShadow: "none"
  },
  ".Select-menu": {
    maxHeight: LIST_HEIGHT,
    overflowY: "auto"
  },
  ".Select-menu div": {
    boxSizing: "content-box"
  },
  ".Select-arrow-zone, .Select-clear-zone": {
    color: theme.palette.action.active,
    cursor: "pointer",
    height: 21,
    width: 21,
    zIndex: 1
  },
  // Only for screen readers. We can't use display none.
  ".Select-aria-only": {
    position: "absolute",
    overflow: "hidden",
    clip: "rect(0 0 0 0)",
    height: 1,
    width: 1,
    margin: -1
  },

  ".Select-menu-dropdown": {
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[2],
    maxHeight: LIST_HEIGHT,
    overflowY: "auto"
  },

  ".Select.is-disabled": {
    color: theme.palette.text.disabled
  },

  ".rc-slider-mark-text": {
    fontFamily: theme.typography.fontFamily
  }
});
